// SPDX-License-Identifier: LicenseRef-Pachamama-Proprietary
// Copyright 2026 Pachamama Studios B.V. All rights reserved.

/**
 * AYNI — the meeting stage on screen (owner, 2026-11-03,
 * `docs/AYNI_XR_MEETING_STAGE_PLAN.md`), in a meeting whose host's plan
 * has the stage (`RoomGrants.stage`). Mounted by `meetingMediaUI.ts`:
 *
 *   - **The stage window** (`mountStageWindow`), for everyone: the
 *     presenter and the people on the stage, each in a tile with their
 *     name — the speaker large (who is talking, read from each feed's
 *     sound level and held a few seconds so it does not flicker; the
 *     presenter while nobody talks), the others small at the half size
 *     (`small` pulls and `resize`, so a tile changing size needs no new
 *     connection). A tile pressed stays large until pressed again. The
 *     presenter and the members see the others, never themselves. A phone
 *     pulls only the speaker's picture and everyone's sound; a headset,
 *     sound only (the dock's speaker button switches it, `meetingSound`).
 *     It folds, goes sound only, and starts muted with one button to hear,
 *     as browsers require.
 *   - **The stage member** (`mountStageMember`), for the person invited:
 *     the invitation card (join with camera and microphone, with the
 *     microphone only, or no thanks; the camera only while it is allowed),
 *     then their preview, microphone and camera switches and Leave the
 *     stage. A camera stopped by the presenter is taken out and the sound
 *     published again; a microphone turned off is muted here (they may
 *     turn it on again); a room that forgot them is published to again.
 *
 * Lives in `src/ui/` so `check:i18n-strings` covers it.
 */

import { t } from '../i18n'
import type { RoomStatus } from '../services/roomClient'
import {
  goLive,
  MeetingMediaError,
  openCameraAndMic,
  openMic,
  stopLive,
  watch,
  type LiveHandle,
  type WatchHandle,
} from '../services/meetingMedia'
import { isVrActive } from '../services/vrSession'

// ------------------------------------------------------------ pure rules

/** A feed's key: the presenter, or a stage place. */
export type FeedKey = 'presenter' | `stage:${number}`

/** A sound level (0..1, linear) above which someone counts as talking. */
export const SPEAKING_LEVEL = 0.04
/** How long someone must be the loudest before they become the speaker. */
export const SPEAK_FOR_MS = 1000
/** The least time a speaker stays large: on a computer, and on a phone, whose picture is pulled again on a change. */
export const SPEAKER_HOLD_MS = 3000
export const PHONE_SPEAKER_HOLD_MS = 8000

export interface SpeakerState {
  speaker: FeedKey | null
  /** When the speaker last changed. */
  since: number
  /** Who has been the loudest since `candidateSince`, while not the speaker. */
  candidate: FeedKey | null
  candidateSince: number
}

export const noSpeaker = (): SpeakerState => ({ speaker: null, since: 0, candidate: null, candidateSince: 0 })

/**
 * The next speaker from the feeds' sound levels now: the loudest above
 * SPEAKING_LEVEL, once loudest for SPEAK_FOR_MS and at least `holdMs`
 * after the last change. Silence changes nothing; a speaker who is no
 * longer a feed is dropped.
 */
export function nextSpeaker(state: SpeakerState, levels: ReadonlyMap<FeedKey, number>, now: number, holdMs: number): SpeakerState {
  const speaker = state.speaker !== null && levels.has(state.speaker) ? state.speaker : null
  let loudest: FeedKey | null = null
  let best = SPEAKING_LEVEL
  for (const [key, level] of levels) {
    if (level >= best) {
      best = level
      loudest = key
    }
  }
  if (loudest === null || loudest === speaker) return { ...state, speaker, candidate: null }
  const candidateSince = state.candidate === loudest ? state.candidateSince : now
  if (now - candidateSince >= SPEAK_FOR_MS && (speaker === null || now - state.since >= holdMs)) {
    return { speaker: loudest, since: now, candidate: null, candidateSince: 0 }
  }
  return { ...state, speaker, candidate: loudest, candidateSince }
}

/**
 * Which feed is large: the one pressed, else the speaker, else the
 * presenter, else the first. Null with no feeds.
 */
export function largeFeed(feeds: readonly FeedKey[], speaker: FeedKey | null, pinned: FeedKey | null): FeedKey | null {
  if (pinned && feeds.includes(pinned)) return pinned
  if (speaker && feeds.includes(speaker)) return speaker
  if (feeds.includes('presenter')) return 'presenter'
  return feeds[0] ?? null
}

/** What a feed is pulled as: the full picture, the half, or the sound only. */
export type FeedSize = 'a' | 'b' | 'audio'

/**
 * What each feed should be: large at the full size and the rest at the
 * half; on a phone the large one only has a picture; in a headset, or
 * sound only, nobody has one.
 */
export function feedSize(key: FeedKey, large: FeedKey | null, mode: 'full' | 'phone' | 'sound'): FeedSize {
  if (mode === 'sound') return 'audio'
  if (key === large) return 'a'
  return mode === 'phone' ? 'audio' : 'b'
}

// ------------------------------------------------------------ helpers

function el<K extends keyof HTMLElementTagNameMap>(tag: K, className = '', text = ''): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag)
  if (className) node.className = className
  if (text) node.textContent = text
  return node
}

function button(className: string, text = ''): HTMLButtonElement {
  const b = el('button', className, text)
  b.type = 'button'
  // Taps on the overlay must not reach the AR session as a select.
  b.addEventListener('beforexrselect', (ev) => ev.preventDefault())
  return b
}

/** The wait before connecting again: doubling from two seconds to fifteen. */
const retryDelay = (attempt: number, tooMany = false): number => (tooMany ? 15_000 : Math.min(2000 * 2 ** attempt, 15_000))

/** Viewers spread their pulls past 50 people, as for the presenter (`meetingMediaUI.pullSpread`). */
const spread = (people: number): number => (people <= 50 ? 0 : Math.floor(Math.random() * Math.min((people / 25) * 1000, 20_000)))

/** A phone's window: the speaker's picture only. */
const phoneWindow = (): boolean => {
  try {
    return window.matchMedia('(max-width: 768px)').matches
  } catch {
    return false
  }
}

export interface StageOptions {
  code: string
  /** The page's sound switch (`meetingSound`): a part to hear, registered; returns its removal. */
  sound: {
    register(part: { present(): boolean; on(): boolean; set(on: boolean): void }): () => void
  }
  /** The presenter's picture arrived for a new broadcast (a headset or phone session is told how to hear it). */
  presenterLive?: () => void
}

/** What the member's card sends the room: accepting the invitation, or declining it or leaving. */
export type StageAnswer = { t: 'stageJoin' } | { t: 'stageLeave' }

// ------------------------------------------------------------ the window

interface Feed {
  key: FeedKey
  place: number | null
  rev: number
  handle: WatchHandle | null
  /** What the pull under way or held is. */
  size: FeedSize | null
  pulling: boolean
  attempt: number
  retry: ReturnType<typeof setTimeout> | null
  level: number
  tile: HTMLButtonElement
  video: HTMLVideoElement
  label: HTMLSpanElement
}

const TICK_MS = 300

export function mountStageWindow(root: HTMLElement, opts: StageOptions) {
  const box = el('div', 'meeting-stage hidden')
  box.setAttribute('role', 'region')
  box.setAttribute('aria-label', t('meeting.stage.label'))
  const bar = el('div', 'meeting-stage-bar')
  const title = el('span', 'meeting-stage-title')
  const listen = button('meeting-presenter-listen meeting-stage-listen hidden', t('meeting.stage.listen'))
  const mode = button('meeting-stage-mode')
  const fold = button('meeting-stage-fold')
  bar.append(title, listen, mode, fold)
  const tiles = el('div', 'meeting-stage-tiles')
  box.append(bar, tiles)
  root.appendChild(box)

  const feeds = new Map<FeedKey, Feed>()
  let ticket: string | null = null
  let people = 0
  let unmuted = false
  let folded = false
  let soundOnly = false
  let pinned: FeedKey | null = null
  let speakers = noSpeaker()
  let names = new Map<FeedKey, string>()

  /** The presenter's broadcast a headset or phone session was last told about. */
  let toldRev: number | null = null

  const viewMode = (): 'full' | 'phone' | 'sound' => (soundOnly || isVrActive() ? 'sound' : phoneWindow() ? 'phone' : 'full')
  const large = (): FeedKey | null => largeFeed([...feeds.keys()], speakers.speaker, pinned)
  const feedOrder = (key: FeedKey): number => (key === 'presenter' ? 0 : Number(key.slice(6)))

  function render(): void {
    box.classList.toggle('hidden', feeds.size === 0)
    box.classList.toggle('folded', folded)
    box.classList.toggle('sound-only', viewMode() === 'sound')
    title.textContent = t('meeting.stage.title', { n: feeds.size })
    fold.textContent = folded ? '▴' : '–'
    fold.setAttribute('aria-label', t(folded ? 'meeting.stage.show' : 'meeting.stage.hide'))
    mode.textContent = t(soundOnly ? 'meeting.presenter.showVideo' : 'meeting.presenter.soundOnly')
    mode.setAttribute('aria-pressed', String(soundOnly))
    listen.classList.toggle('hidden', unmuted || feeds.size === 0)
    const big = large()
    for (const f of feeds.values()) {
      const name = names.get(f.key) ?? (f.key === 'presenter' ? t('meeting.presenter.label') : t('meeting.stage.guest'))
      f.label.textContent = name
      f.tile.setAttribute('aria-label', t(pinned === f.key ? 'meeting.stage.unpin' : 'meeting.stage.pin', { name }))
      f.tile.setAttribute('aria-pressed', String(pinned === f.key))
      f.tile.classList.toggle('large', f.key === big)
      f.tile.classList.toggle('audio', f.size === 'audio' || f.size === null)
      f.tile.classList.toggle('pinned', pinned === f.key)
    }
    // The large tile last, under the row of small ones.
    const order = [...feeds.values()].sort((a, b) => Number(a.key === big) - Number(b.key === big) || feedOrder(a.key) - feedOrder(b.key))
    for (const f of order) tiles.appendChild(f.tile)
  }

  function newFeed(key: FeedKey, place: number | null, rev: number): Feed {
    const tile = button('meeting-stage-tile audio')
    const video = el('video')
    video.muted = !unmuted
    video.playsInline = true
    video.autoplay = true
    video.setAttribute('playsinline', '')
    const label = el('span', 'meeting-stage-name')
    tile.append(video, label)
    tile.addEventListener('click', () => {
      pinned = pinned === key ? null : key
      apply()
    })
    return { key, place, rev, handle: null, size: null, pulling: false, attempt: 0, retry: null, level: 0, tile, video, label }
  }

  function drop(key: FeedKey): void {
    const f = feeds.get(key)
    if (!f) return
    if (f.retry !== null) clearTimeout(f.retry)
    f.handle?.close()
    f.video.srcObject = null
    f.tile.remove()
    feeds.delete(key)
    if (pinned === key) pinned = null
  }

  function later(f: Feed, delay: number): void {
    if (f.retry !== null) clearTimeout(f.retry)
    f.retry = setTimeout(() => {
      f.retry = null
      void pull(f)
    }, delay + spread(people))
  }

  /** Pull the feed as it should be now, replacing what it holds. */
  async function pull(f: Feed): Promise<void> {
    if (f.pulling || !ticket || feeds.get(f.key) !== f) return
    const want = feedSize(f.key, large(), viewMode())
    const rev = f.rev
    f.pulling = true
    try {
      const handle = await watch(opts.code, ticket, {
        ...(f.place !== null ? { stage: f.place } : {}),
        ...(want === 'audio' ? { audioOnly: true } : {}),
        ...(want === 'b' ? { small: true } : {}),
      })
      if (feeds.get(f.key) !== f || f.rev !== rev) {
        handle.close()
        return
      }
      f.handle?.close()
      f.handle = handle
      f.size = want
      f.attempt = 0
      handle.onDrop(() => {
        if (f.handle !== handle) return
        later(f, retryDelay(f.attempt++))
      })
      f.video.srcObject = handle.stream
      void f.video.play()?.catch(() => undefined)
      // Once per broadcast (not on every reconnection): a headset can't see this window.
      if (f.key === 'presenter' && toldRev !== rev) {
        toldRev = rev
        opts.presenterLive?.()
      }
      render()
    } catch (err) {
      if (feeds.get(f.key) !== f || f.rev !== rev) return
      const reason = err instanceof MeetingMediaError ? err.reason : 'unavailable'
      // A stale ticket: a new one comes with the room's next welcome.
      if (reason === 'not_in_room') return
      later(f, retryDelay(f.attempt++, reason === 'too_many'))
    } finally {
      f.pulling = false
      // A newer revision arrived meanwhile: pull that; or the size it should be changed: fit it.
      if (feeds.get(f.key) === f && f.retry === null) {
        if (f.rev !== rev) void pull(f)
        else if (f.handle && f.size !== feedSize(f.key, large(), viewMode())) void fit(f)
      }
    }
  }

  /** Bring a feed to the size it should be: a resize between the two pictures, else a new pull. */
  async function fit(f: Feed): Promise<void> {
    if (f.pulling || f.retry !== null) return
    const want = feedSize(f.key, large(), viewMode())
    if (!f.handle) {
      void pull(f)
      return
    }
    if (f.size === want) return
    if (f.size !== 'audio' && f.size !== null && want !== 'audio') {
      const handle = f.handle
      f.size = want
      render()
      if (await handle.resize(want)) return
      if (f.handle !== handle) return
    }
    void pull(f)
  }

  function apply(): void {
    render()
    for (const f of feeds.values()) void fit(f)
  }

  // The speaker, from the sound levels, a few times a second.
  const tick = setInterval(() => {
    if (feeds.size === 0) return
    const levels = new Map<FeedKey, number>()
    for (const f of feeds.values()) {
      const now = f.handle?.audioLevel() ?? 0
      f.level = f.level * 0.6 + now * 0.4
      levels.set(f.key, f.level)
      f.tile.classList.toggle('speaking', f.level >= SPEAKING_LEVEL)
    }
    const before = large()
    speakers = nextSpeaker(speakers, levels, Date.now(), viewMode() === 'phone' ? PHONE_SPEAKER_HOLD_MS : SPEAKER_HOLD_MS)
    // The mode follows a headset session starting or ending, and the window's width.
    if (large() !== before || [...feeds.values()].some((f) => f.handle && !f.pulling && f.size !== feedSize(f.key, large(), viewMode()))) apply()
  }, TICK_MS)

  const setSound = (on: boolean): void => {
    unmuted = on
    for (const f of feeds.values()) {
      f.video.muted = !on
      if (on) void f.video.play()?.catch(() => undefined)
    }
    render()
  }
  listen.addEventListener('click', () => setSound(true))
  const unregister = opts.sound.register({ present: () => feeds.size > 0, on: () => unmuted, set: setSound })
  fold.addEventListener('click', () => {
    folded = !folded
    render()
  })
  mode.addEventListener('click', () => {
    soundOnly = !soundOnly
    apply()
  })
  const onOnline = (): void => {
    for (const f of feeds.values()) {
      if (f.retry !== null) {
        clearTimeout(f.retry)
        f.retry = null
        void pull(f)
      }
    }
  }
  window.addEventListener('online', onOnline)

  return {
    update(status: RoomStatus | null) {
      ticket = status?.ticket ?? null
      people = status?.count ?? 0
      const live = new Map<FeedKey, { place: number | null; rev: number }>()
      if (status?.connected && status.meeting) {
        // Everyone but the presenter sees the presenter; nobody sees themselves.
        if (status.mediaRev !== null && status.seat !== 'presenter') live.set('presenter', { place: null, rev: status.mediaRev })
        const mine = status.myStage?.joined ? status.myStage.place : null
        for (const s of status.stageLive) if (s.place !== mine) live.set(`stage:${s.place}`, { place: s.place, rev: s.rev })
      }
      names = new Map(status?.stage.map((m) => [`stage:${m.place}` as FeedKey, m.name ?? t('meeting.stage.guest')]) ?? [])
      for (const key of [...feeds.keys()]) if (!live.has(key)) drop(key)
      for (const [key, { place, rev }] of live) {
        const f = feeds.get(key)
        if (!f) {
          const next = newFeed(key, place, rev)
          feeds.set(key, next)
          render()
          later(next, 0)
        } else if (f.rev !== rev) {
          f.rev = rev
          f.attempt = 0
          later(f, 0)
        }
      }
      render()
    },
    dispose() {
      clearInterval(tick)
      unregister()
      window.removeEventListener('online', onOnline)
      for (const key of [...feeds.keys()]) drop(key)
      box.remove()
    },
  }
}

// ------------------------------------------------------------ the member

/** How long a note stays once its person is off the stage ("You were taken off the stage"). */
const NOTE_OFF_STAGE_MS = 8000

export function mountStageMember(root: HTMLElement, opts: StageOptions & { send: (m: StageAnswer) => void }) {
  const box = el('div', 'meeting-live meeting-stage-member hidden')
  box.setAttribute('role', 'region')
  box.setAttribute('aria-label', t('meeting.stage.member.aria'))

  // The invitation.
  const invite = el('div', 'meeting-stage-invite hidden')
  const inviteTitle = el('strong', '', t('meeting.stage.invite.title'))
  const inviteText = el('p', 'meeting-driver-mic-consent', t('meeting.stage.invite.text'))
  const inviteNoCamera = el('p', 'meeting-driver-mic-consent hidden', t('meeting.stage.invite.noCamera'))
  const joinCamera = button('meeting-live-start', t('meeting.stage.invite.camera'))
  const joinMic = button('meeting-live-start', t('meeting.stage.invite.mic'))
  const decline = button('', t('meeting.stage.invite.decline'))
  const inviteRow = el('div', 'meeting-live-buttons')
  inviteRow.append(joinCamera, joinMic, decline)
  invite.append(inviteTitle, inviteText, inviteNoCamera, inviteRow)

  // On the stage, before going live (a reload, a camera refused) and while live.
  const start = el('div', 'meeting-live-buttons hidden')
  const startCamera = button('meeting-live-start', t('meeting.stage.invite.camera'))
  const startMic = button('meeting-live-start', t('meeting.stage.invite.mic'))
  start.append(startCamera, startMic)
  const live = el('div', 'meeting-live-on hidden')
  const preview = el('video')
  preview.muted = true
  preview.playsInline = true
  preview.autoplay = true
  preview.setAttribute('playsinline', '')
  preview.setAttribute('aria-label', t('meeting.live.preview.aria'))
  const badge = el('span', 'meeting-live-badge', t('meeting.stage.badge'))
  const mic = button('')
  const camera = button('')
  const row = el('div', 'meeting-live-buttons')
  row.append(mic, camera)
  live.append(preview, badge, row)
  const leave = button('meeting-stage-leave hidden', t('meeting.stage.leave'))
  const message = el('p', 'meeting-live-message')
  message.setAttribute('role', 'status')
  box.append(invite, start, live, leave, message)
  root.appendChild(box)

  let status: RoomStatus | null = null
  let stream: MediaStream | null = null
  let handle: LiveHandle | null = null
  let busy = false
  /** What the person chose: to be live, and with the camera. Kept across the room's reconnections. */
  let want: { camera: boolean } | null = null
  /** The room listed this place live once: from then on, its absence means it forgot (publish again). */
  let seenLive = false
  let noticeSeen = 0
  let reconnectTimer: ReturnType<typeof setTimeout> | null = null
  let noteTimer: ReturnType<typeof setTimeout> | null = null
  let attempt = 0

  const place = (): number | null => (status?.myStage?.joined ? status.myStage.place : null)
  const cameraAllowed = (): boolean => !!status?.myStage?.camera
  /** A note under the card; off the stage it goes by itself after a while. */
  const say = (text: string): void => {
    message.textContent = text
    if (noteTimer !== null) clearTimeout(noteTimer)
    noteTimer = null
    if (text && !status?.myStage) {
      noteTimer = setTimeout(() => {
        noteTimer = null
        if (!status?.myStage) {
          message.textContent = ''
          render()
        }
      }, NOTE_OFF_STAGE_MS)
    }
  }

  function labels(): void {
    const audio = stream?.getAudioTracks()[0]
    const video = stream?.getVideoTracks()[0]
    mic.textContent = t(audio && !audio.enabled ? 'meeting.live.mic.unmute' : 'meeting.live.mic.mute')
    mic.setAttribute('aria-pressed', String(!!audio && !audio.enabled))
    // Without a picture: offered when the camera is allowed (published again with it).
    camera.textContent = t(video ? (video.enabled ? 'meeting.live.camera.off' : 'meeting.live.camera.on') : 'meeting.live.camera.on')
    camera.setAttribute('aria-pressed', String(!video || !video.enabled))
    camera.classList.toggle('hidden', !video && !cameraAllowed())
    preview.classList.toggle('hidden', !video)
  }

  function render(): void {
    const my = status?.myStage ?? null
    box.classList.toggle('hidden', !my && !message.textContent)
    invite.classList.toggle('hidden', !my || my.joined)
    joinCamera.classList.toggle('hidden', !my?.camera)
    inviteNoCamera.classList.toggle('hidden', !my || my.camera)
    const onStage = !!my?.joined
    live.classList.toggle('hidden', !onStage || !handle)
    start.classList.toggle('hidden', !onStage || !!handle || busy || want !== null)
    startCamera.classList.toggle('hidden', !my?.camera)
    leave.classList.toggle('hidden', !onStage)
    labels()
  }

  function stopStream(): void {
    for (const track of stream?.getTracks() ?? []) track.stop()
    stream = null
    preview.srcObject = null
  }

  /** Stop sending; `tellRoom` for a stop the person chose (the room keeps their place). */
  function end(tellRoom: boolean): void {
    if (reconnectTimer !== null) clearTimeout(reconnectTimer)
    reconnectTimer = null
    const was = !!handle
    handle?.close()
    handle = null
    stopStream()
    seenLive = false
    const n = place()
    if (tellRoom && was && n !== null && status?.ticket) void stopLive(opts.code, '', undefined, { kind: 'stage', place: n, ticket: status.ticket })
    render()
  }

  /** Publish as the stage member, with the camera when chosen and allowed. */
  async function publish(): Promise<void> {
    const n = place()
    const ticket = status?.ticket
    if (busy || !want || n === null || !ticket) return
    busy = true
    render()
    try {
      const withCamera = want.camera && cameraAllowed()
      if (!stream || (!!stream.getVideoTracks()[0]) !== withCamera) {
        stopStream()
        stream = withCamera ? await openCameraAndMic() : await openMic()
      }
      const next = await goLive(opts.code, '', stream, undefined, undefined, { kind: 'stage', place: n, ticket })
      if (!want || place() !== n) {
        next.close()
        return
      }
      handle?.close()
      handle = next
      seenLive = false
      attempt = 0
      next.onDrop(() => {
        if (handle === next) again()
      })
      if (stream.getVideoTracks()[0]) {
        preview.srcObject = stream
        void preview.play()?.catch(() => undefined)
      }
      say('')
    } catch (err) {
      const reason = err instanceof MeetingMediaError ? err.reason : 'unavailable'
      if (reason === 'denied') {
        // The browser refused: they choose again (the microphone alone, perhaps).
        want = null
        stopStream()
        say(t('meeting.stage.error.denied'))
      } else if (reason === 'not_on_stage') {
        want = null
        stopStream()
      } else if (reason === 'sound_only') {
        // The camera was stopped meanwhile: the sound alone.
        want = { camera: false }
        again()
      } else {
        again(reason === 'too_many')
      }
    } finally {
      busy = false
      render()
    }
  }

  /** Publish again, at once the first time and then more slowly. */
  function again(tooMany = false): void {
    if (reconnectTimer !== null || !want) return
    say(t('meeting.live.reconnecting'))
    const delay = attempt === 0 ? 0 : retryDelay(attempt - 1, tooMany)
    attempt++
    reconnectTimer = setTimeout(() => {
      reconnectTimer = null
      void publish()
    }, delay)
  }

  function choose(withCamera: boolean): void {
    want = { camera: withCamera }
    say('')
    // Invited: accept first; the room's answer (joined) starts the publication.
    if (status?.myStage && !status.myStage.joined) opts.send({ t: 'stageJoin' })
    else void publish()
    render()
  }

  joinCamera.addEventListener('click', () => choose(true))
  joinMic.addEventListener('click', () => choose(false))
  startCamera.addEventListener('click', () => choose(true))
  startMic.addEventListener('click', () => choose(false))
  decline.addEventListener('click', () => {
    want = null
    opts.send({ t: 'stageLeave' })
  })
  leave.addEventListener('click', () => {
    want = null
    end(true)
    opts.send({ t: 'stageLeave' })
  })
  mic.addEventListener('click', () => {
    const track = stream?.getAudioTracks()[0]
    if (track) track.enabled = !track.enabled
    // Turned on again after the presenter turned it off: the note has done its work.
    if (track?.enabled && message.textContent === t('meeting.stage.muted')) say('')
    labels()
  })
  camera.addEventListener('click', () => {
    const track = stream?.getVideoTracks()[0]
    if (track) {
      track.enabled = !track.enabled
      labels()
      return
    }
    // No picture yet and the camera is now allowed: published again with it.
    if (!cameraAllowed()) return
    want = { camera: true }
    handle?.close()
    handle = null
    void publish()
  })

  return {
    update(next: RoomStatus | null) {
      const before = status?.myStage ?? null
      status = next
      const my = next?.myStage ?? null
      // Off the stage (left, declined, taken off, or the meeting is gone).
      if (!my || !next?.connected) {
        if (handle || stream) end(false)
        if (!my) want = null
      }
      const notice = next?.stageNotice
      if (notice && notice.n !== noticeSeen) {
        noticeSeen = notice.n
        if (notice.why === 'removed') say(t('meeting.stage.removed'))
        if (notice.why === 'muted') {
          const track = stream?.getAudioTracks()[0]
          if (track) track.enabled = false
          say(t('meeting.stage.muted'))
        }
      }
      if (my?.joined && next?.connected) {
        // The camera stopped by the presenter: the room dropped the picture; the sound goes out again.
        if (stream?.getVideoTracks()[0] && !my.camera) {
          want = { camera: false }
          handle?.close()
          handle = null
          say(t('meeting.stage.cameraStopped'))
        }
        if (!before?.camera && my.camera && before?.joined) say(t('meeting.stage.cameraAllowed'))
        const listed = next.stageLive.some((s) => s.place === my.place)
        if (handle && listed) seenLive = true
        else if (handle && !busy && seenLive && !listed) {
          // The room forgot (this connection dropped and came back): again.
          seenLive = false
          handle.close()
          handle = null
        }
        if (want && !handle && !busy && reconnectTimer === null) void publish()
      }
      render()
    },
    dispose() {
      if (noteTimer !== null) clearTimeout(noteTimer)
      end(false)
      box.remove()
    },
  }
}
